/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement, useState } from "react";

import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import {
  IPersonaProps,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
} from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import PeoplePickerController from "Components/ReactHookForm/PeoplePickerController/PeoplePickerController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { SidePanelConstants } from "Constants/ApplicationConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  bulkAssignApplication,
  bulkAssignApplicationReset,
  getAvailableAssignApplication,
} from "Dataflows/Application/ApplicationAction";
import {
  getBulkAssignApplicationErrorMessage,
  getAvailableAssignApplicationInfo,
  getAvailableAssignApplicationInfoErrorMessage,
  getAvailableAssignApplicationInfoStatus,
  getBulkAssignApplicationStatus,
} from "Dataflows/Application/ApplicationSelector";
import { IApplicationAssignTechnologyAssignment } from "Interfaces/Application/IApplicationAssign";

import { IApplicationBulkAssignSidePanel as IApplicationBulkAssignSidePanelProps } from "./IApplicationBulkAssignSidePanel";
import { IApplicationGetBulkAssignUser } from "Interfaces/Application/IApplicationGetBulkAssign";

export const ApplicationBulkAssignSidePanel = (
  props: IApplicationBulkAssignSidePanelProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, applicationFormIds, onSubmitSuccess } = props;
  const dispatch = useDispatch();
  const assignApplicationInfoErrorMessage = useSelector(
    getAvailableAssignApplicationInfoErrorMessage
  );
  const assignApplicationErrorMessage = useSelector(getBulkAssignApplicationErrorMessage);
  const assignApplicationInfoStatus = useSelector(getAvailableAssignApplicationInfoStatus);
  const assignApplicationStatus = useSelector(getBulkAssignApplicationStatus);

  useEffect(() => {
    if (isPanelOpen && assignApplicationStatus === OperationStatus.Idle) {
      dispatch(getAvailableAssignApplication(applicationFormIds, TenantTypesEnum.MVP));
    }
  }, [applicationFormIds, isPanelOpen]);

  const assignApplicationInfo = useSelector(getAvailableAssignApplicationInfo);

  const mapAssigneesToOptions = (assignees: IApplicationGetBulkAssignUser[]): IPersonaProps[] => {
    return assignees.map((assignee) => {
      return {
        text: `${assignee.firstName} ${assignee.lastName}`,
        secondaryText: assignee.emailAddress,
      };
    });
  };

  const { account, hasRole } = useAuth();

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const mapAssignApplicationInfoToFormValues = () => ({
    technologyAssignments: assignApplicationInfo?.technologyAssignments.map((assignment) => ({
      ...assignment,
      currentAssignee: assignment.currentAssignee?.emailAddress,
      hasScore: assignment.currentAssignee?.hasScore,
      canAssign:
        hasRole(RolesEnum.MVP_PROGRAM_OWNER) ||
        (hasRole(RolesEnum.MVP_TECH_AREA_VALIDATOR) &&
          assignment.availableAssignees.find((aa) => aa.emailAddress === account?.userName)),
    })),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: mapAssignApplicationInfoToFormValues(),
  });

  useEffect(() => {
    if (assignApplicationInfo) {
      reset(mapAssignApplicationInfoToFormValues());
    }
  }, [assignApplicationInfo]);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () =>
    handleSubmit((formData) => {
      // Strip off availableAssignees
      const technologyAssignments = formData.technologyAssignments?.map((assignment) => ({
        technologyFocusArea: assignment.technologyFocusArea,
        emailAddress: assignment.currentAssignee || "",
      })) as IApplicationAssignTechnologyAssignment[];

      dispatch(
        bulkAssignApplication(applicationFormIds, technologyAssignments, TenantTypesEnum.MVP)
      );
    });

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    dispatch(bulkAssignApplicationReset());
  };

  useEffect(() => {
    if (assignApplicationStatus === OperationStatus.Succeeded) {
      dismissPanel();
      onSubmitSuccess();
    }
  }, [assignApplicationStatus]);

  const { fields } = useFieldArray({ control, name: "technologyAssignments" });

  const isSaving = assignApplicationStatus === OperationStatus.Pending;

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Assign Application"
        saveButtonLabel="Save"
        error={assignApplicationErrorMessage}
        disableActions={
          assignApplicationInfo?.technologyAssignments?.every(
            (ta) => ta.currentAssignee?.hasScore
          ) || isSaving
        }
        onDismiss={dismissPanel}
        onSave={save()}
      >
        {assignApplicationInfoStatus === OperationStatus.Pending ? (
          <Spinner size={SpinnerSize.large} />
        ) : !!assignApplicationInfoErrorMessage ? (
          <h5>{assignApplicationInfoErrorMessage}</h5>
        ) : (
          <Stack tokens={{ childrenGap: "1rem" }}>
            <MessageBar messageBarType={MessageBarType.info}>
              {SidePanelConstants.SidePanelInformation}
            </MessageBar>
            {fields.map((field, index) => {
              const assigneesOptions = mapAssigneesToOptions(field.availableAssignees);
              console.log("assigneesOptions", assigneesOptions);
              return (
                <StackItem key={field.id}>
                  {assigneesOptions.length === 0 ? (
                    <MessageBar messageBarType={MessageBarType.error}>
                      {SidePanelConstants.NoAssigneesErrorMessage}
                    </MessageBar>
                  ) : (
                    <PeoplePickerController
                      control={control}
                      inputName={`technologyAssignments[${index}].currentAssignee`}
                      errors={errors}
                      labelDisplayName={`Assign ${field.technologyFocusArea}`}
                      isDisabled={field.hasScore || isSaving || !field.canAssign}
                      options={mapAssigneesToOptions(field.availableAssignees)}
                      keyPropertyName="secondaryText"
                      defaultSelectedKey={field.currentAssignee}
                    />
                  )}
                </StackItem>
              );
            })}
          </Stack>
        )}
      </SidePanel>
    </form>
  );
};
